import React, {useCallback, useEffect, useState} from 'react'
import {Box} from '@mui/material'
import DataTable, {PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import Button from 'src/components-new/primitives/Button'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {buildColumns, sortSamplesByStatusAndName, BiologicalSampleRow} from './biologicalSamplesColumns'
import RequestSampleDialogNew, {RequestSampleValues} from './RequestSampleDialogNew'

const DEFAULT_SUBJECT = 'campione'

// The backend's `sort` param can only order by a single real field, but the default
// ordering needs a custom status-lifecycle rank (see sortSamplesByStatusAndName) that
// isn't a plain field sort — so, same as PatientsListNew, sorting and pagination both
// happen client-side over a large fetched batch rather than a per-page server request.
// 200 mirrors the cap used there; a filtered result set larger than that won't be fully
// sortable/paginatable client-side.
const FETCH_SIZE = 200

/**
 * New-style equivalent of src/pages/testsDBS/TestsDBS.tsx. No page title/breadcrumb is
 * rendered here — HeaderBar derives it from the "biologicalSamples" nav item label — and
 * there is no row click / detail view, matching the legacy behavior.
 */
export default function BiologicalSamplesListNew(): JSX.Element {
  const configuration = useAppSelector(state => state.pspConfigReducer.data.biologicalSamplesConfiguration)
  const toast = useToast()

  const [rows, setRows] = useState<BiologicalSampleRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10})
  const [requestOpen, setRequestOpen] = useState(false)

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getTestDbs(activeFilters, setStatus, {page: 0, rowsPerPage: FETCH_SIZE, order: []}),
      apiClient.getTestDbsCount(activeFilters, setStatusCount),
    ])
      .then(([data, countResult]) => {
        if (Array.isArray(data)) {
          setRows(data.map((row: any) => {
            const sampleData = row.samples && row.samples[0]
            return {
              ...row,
              // Surname before first name — matches the list's sort order (status, then
              // surname, then first name), consistent with PatientsListNew's getDisplayName.
              patient: `${row.patientLastName ?? ''} ${row.patientFirstName ?? ''}`.trim(),
              readableSample: sampleData && `${sampleData.label}: ${sampleData.value}`,
            }
          }))
        }
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [activeFilters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleSaveRequest = async ({patient}: RequestSampleValues) => {
    let finalStatus: string | undefined
    await apiClient.postTestDbs(patient._id, (s: string) => { finalStatus = s })
    if (finalStatus === STATUS.FULFILLED) {
      toast.success('Richiesta inviata con successo.')
      setRequestOpen(false)
      fetchData()
    } else {
      toast.error('Errore nella richiesta del campione.')
    }
  }

  const filterConfig: FilterItem[] = configuration?.filters ?? []
  const columns = buildColumns(configuration?.columns)
  const subject = configuration?.subject ?? DEFAULT_SUBJECT

  const sortedRows = sortSamplesByStatusAndName(rows)
  const pagedRows = sortedRows.slice(
    pagination.page * pagination.rowsPerPage,
    (pagination.page + 1) * pagination.rowsPerPage,
  )

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, flexWrap: 'wrap'}}>
        <Button onClick={() => setRequestOpen(true)} variant="primary">
          {`Richiedi ${subject}`}
        </Button>
        <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel recuperare i campioni biologici.'}
        </InlineMessage>
      )}

      <DataTable<BiologicalSampleRow>
        columns={columns}
        rows={pagedRows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessun campione trovato"
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
      />

      {requestOpen && (
        <RequestSampleDialogNew
          open={requestOpen}
          subject={subject}
          onClose={() => setRequestOpen(false)}
          onSave={handleSaveRequest}
        />
      )}
    </Box>
  )
}